import {
  ATTACHMENT_SCAN_STATUS_LABELS,
  isPreviewableAttachment,
  type TicketAttachment,
} from '@gb-ticket/shared-types';
import { EyeIcon, PaperclipIcon, XIcon } from 'lucide-react';
import { useState } from 'react';

import { FilePickerButton } from '@/components/file-picker-button';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { formatBytes } from '@/lib/format';

export function TicketAttachments({
  ticketRef,
  items,
  canUpload,
  uploading,
  onUpload,
}: {
  ticketRef: string;
  items: TicketAttachment[];
  canUpload: boolean;
  uploading: boolean;
  onUpload: (file: File) => Promise<void>;
}) {
  const [preview, setPreview] = useState<TicketAttachment | null>(null);
  const [chosenName, setChosenName] = useState<string | null>(null);

  return (
    <div className="space-y-3">
      {items.length === 0 ? (
        <p className="text-sm text-muted-foreground">Keine Anhänge.</p>
      ) : (
        <ul className="space-y-2 text-sm">
          {items.map((file) => {
            const href = `/api/tickets/${ticketRef}/attachments/${file.id}`;
            const pending = file.scanStatus === 'PENDING';
            const previewable = isPreviewableAttachment(file.mimeType) && !pending;
            return (
              <li key={file.id} className="flex flex-wrap items-center gap-2">
                <PaperclipIcon className="size-4 shrink-0" aria-hidden />
                {pending ? (
                  <span className="font-medium">{file.fileName}</span>
                ) : (
                  <a
                    className="font-medium underline-offset-4 hover:text-foreground hover:underline"
                    href={href}
                  >
                    {file.fileName}
                  </a>
                )}
                <span className="text-muted-foreground">
                  ({formatBytes(file.sizeBytes)} · {file.uploadedBy.displayName})
                </span>
                {file.scanStatus !== 'CLEAN' && (
                  <Badge variant="outline">{ATTACHMENT_SCAN_STATUS_LABELS[file.scanStatus]}</Badge>
                )}
                {previewable && (
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => setPreview(file)}
                  >
                    <EyeIcon />
                    Vorschau
                  </Button>
                )}
              </li>
            );
          })}
        </ul>
      )}
      {canUpload && (
        <>
          <label htmlFor="ticket-attachment" className="sr-only">
            Datei anhängen
          </label>
          <FilePickerButton
            id="ticket-attachment"
            disabled={uploading}
            chosenLabel={chosenName ?? 'Keine Datei ausgewählt'}
            onPick={(list) => {
              const file = list?.[0];
              if (!file) {
                return;
              }
              setChosenName(file.name);
              void onUpload(file).finally(() => setChosenName(null));
            }}
          />
        </>
      )}
      {preview && (
        <AttachmentPreview ticketRef={ticketRef} file={preview} onClose={() => setPreview(null)} />
      )}
    </div>
  );
}

function AttachmentPreview({
  ticketRef,
  file,
  onClose,
}: {
  ticketRef: string;
  file: TicketAttachment;
  onClose: () => void;
}) {
  const src = `/api/tickets/${ticketRef}/attachments/${file.id}?inline=1`;
  const isPdf = file.mimeType === 'application/pdf';

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="attachment-preview-title"
      onClick={onClose}
    >
      <div
        className="flex max-h-[90vh] w-full max-w-5xl flex-col overflow-hidden rounded-xl border bg-background shadow-lg"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-center justify-between gap-3 border-b px-4 py-3">
          <h2 id="attachment-preview-title" className="truncate text-sm font-medium">
            {file.fileName}
          </h2>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            onClick={onClose}
            aria-label="Vorschau schließen"
          >
            <XIcon />
          </Button>
        </div>
        <div className="min-h-0 flex-1 overflow-auto bg-muted/40 p-3">
          {isPdf ? (
            <iframe
              title={file.fileName}
              src={src}
              className="h-[70vh] w-full rounded-md border bg-background"
            />
          ) : (
            <img
              src={src}
              alt={file.fileName}
              className="mx-auto max-h-[70vh] max-w-full object-contain"
            />
          )}
        </div>
      </div>
    </div>
  );
}
